import { useMemo } from 'react';
import { compact, isEmpty } from 'lodash';
import { Box, HStack, Pressable } from 'native-base';
import { useTranslation } from 'react-i18next';

import { ArrowSquareUpRightIcon, Text, UserStarIcon } from '@pimm/base';
import { Contact } from '@pimm/services/lib/sms-tenants';
import { ResourceLoader } from '@app/components/shared';
import { useStoreSettings } from '../context/store-settings.context';
import { ReadOnlyField } from './read-only-field';
import Section from './section';

type ContactBlockProps = {
  contact?: Contact;
};

export const ContactBlock = ({ contact }: ContactBlockProps) => {
  const { t } = useTranslation();
  const site = useStoreSettings(_ => _.site);

  const isContactEmpty = useMemo(() => {
    if (!contact) return true;
    return (
      isEmpty(contact.firstName) &&
      isEmpty(contact.lastName) &&
      isEmpty(contact.email) &&
      isEmpty(contact.phone) &&
      isEmpty(contact.mobilePhone)
    );
  }, [contact]);

  const fullName = compact([contact?.firstName, contact?.lastName])
    .map(_ => _?.trim())
    .join(' ');

  const handleOpenTeamRoster = () => {
    window.open(`${window.location.origin}/${site.id}/store-settings/team-mgmt/team-roster`, '_blank', 'noreferrer');
  };

  return (
    <Section.Card
      variant="form"
      title={t('store_settings:primary_contact')}
      subtitle={
        <Pressable onPress={handleOpenTeamRoster}>
          <HStack space={1} alignItems="center">
            <Text size="sm" color="blue.500">
              {t('store_settings:primary_contact_managed_hint')}
            </Text>
            <ArrowSquareUpRightIcon size="xs" color="blue.500" />
          </HStack>
        </Pressable>
      }
      icon={
        <Box alignSelf="flex-start">
          <UserStarIcon size={4} color="gray.700" />
        </Box>
      }
    >
      <ResourceLoader py={2} isEmpty={isContactEmpty} emptyMessage={t('store_settings:empty_primary_contact_read_only')}>
        <HStack space={6} flexWrap="wrap">
          <Box flex={1} minW={170}>
            <ReadOnlyField label={t('store_settings:name')} value={fullName} />
          </Box>

          <Box flex={1} minW={170}>
            <ReadOnlyField label={t('store_settings:email')} value={contact?.email} />
          </Box>

          <Box flex={1} minW={170}>
            <ReadOnlyField label={t('store_settings:phone')} value={contact?.phone} />
          </Box>

          <Box flex={1} minW={170}>
            <ReadOnlyField label={t('store_settings:mobile_phone')} value={contact?.mobilePhone} />
          </Box>
        </HStack>
      </ResourceLoader>
    </Section.Card>
  );
};
